import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, CardMedia, Stack, styled, Typography } from '@mui/material'
import type { FC } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { OwnershipDto } from '@hcr/api/consumer'
import { Alert, COLORS, ICONS, remUnit, unit } from '@hcr/ui'

import { SvgPercentageStamp } from '../../../../../../assets/images'
import { useUserDetails } from '../../../../../../contexts'
import { useBenefitsQuery } from '../../../../../../hooks/api/optimizely/queries'

import { BenefitsLoading } from './BenefitsLoading'
import { getBenefitLinkElementId } from './common/benefit-preview'
import { createBenefitsGroup } from './common/benefits-group/createBenefitsGroup'

const MAX_VISIBLE_BENEFIT_ITEMS = 5

const benefitsGroup = createBenefitsGroup()

export const Benefits: FC = () => {
  const [isExpanded, setExpanded] = useState<boolean>(false)
  const wasExpandedRef = useRef(false)
  const headingId = useId()

  const { t } = useTranslation()
  const userDetails = useUserDetails()

  const { is_villas, is_active_gold, is_b2b_gold, is_b2b_villas } = (userDetails.data?.ownership as OwnershipDto) || {}
  const isVillasOwnerNoBenefit = Boolean(is_villas) && Boolean(!userDetails.data?.has_benefits)

  const benefits = useBenefitsQuery(
    {
      isGoldOwner: (Boolean(is_active_gold) || Boolean(is_b2b_gold)) && Boolean(userDetails.data?.has_benefits),
      isVillasOwner: (Boolean(is_villas) || Boolean(is_b2b_villas)) && Boolean(userDetails.data?.has_benefits),
      isVillasOwnerNoBenefit,
    },
    { enabled: userDetails.isSuccess }
  )

  const flatBenefits = benefits.data?.content.flatMap(group => group.content) ?? []
  const hasHiddenBenefits = flatBenefits.length > MAX_VISIBLE_BENEFIT_ITEMS
  const firstHiddenBenefitId = flatBenefits[MAX_VISIBLE_BENEFIT_ITEMS]?.benefitId

  useEffect(() => {
    if (isExpanded && !wasExpandedRef.current && firstHiddenBenefitId != null) {
      document.getElementById(getBenefitLinkElementId(firstHiddenBenefitId))?.focus()
    }
    wasExpandedRef.current = isExpanded
  }, [isExpanded, firstHiddenBenefitId])

  if (userDetails.isError || benefits.isError) {
    return <Alert severity='error' title={t('error.error')} description={t('error.generic')} />
  }

  if (userDetails.isSuccess && benefits.isSuccess) {
    const visibleGroups = isExpanded
      ? benefits.data.content
      : benefits.data.content.reduce<{ groups: typeof benefits.data.content; remaining: number }>(
          (acc, group) => {
            const content = group.content.slice(0, acc.remaining)
            return content.length > 0
              ? { remaining: acc.remaining - content.length, groups: [...acc.groups, { ...group, content }] }
              : acc
          },
          { remaining: MAX_VISIBLE_BENEFIT_ITEMS, groups: [] }
        ).groups

    return (
      <Stack
        position='relative'
        border={`${unit(0.25)} solid ${COLORS.vaalea[700]}`}
        role='region'
        aria-labelledby={headingId}
      >
        <HeroImage image={benefits.data.imageUrl ?? undefined} aria-hidden='true' />
        <Box position='absolute' top={unit(2.75)} right={unit(2.75)} aria-hidden='true'>
          <SvgPercentageStamp />
        </Box>
        <Stack direction='column' gap={unit(2.5)} paddingTop={unit(4.5)} paddingBottom={unit(6)} paddingX={unit(4)}>
          <Title variant='headlineM' component='h2' id={headingId}>
            {benefits.data.title}
          </Title>
          <Description variant='bodyM'>{benefits.data.description}</Description>
        </Stack>

        {visibleGroups.map(benefitsGroup)}

        {hasHiddenBenefits && (
          <ToggleButton type='button' aria-expanded={isExpanded} onClick={() => setExpanded(!isExpanded)}>
            <Stack direction='row' gap={unit(2.5)} justifyContent='center' alignItems='center'>
              <ToggleLabel variant='labelM' color={COLORS.burgundi[500]}>
                {isExpanded ? t('owner.show-less') : t('owner.show-more')}
              </ToggleLabel>
              <FontAwesomeIcon
                size={'sm'}
                icon={isExpanded ? ICONS.fasArrowUp : ICONS.fasArrowDown}
                color={COLORS.burgundi[500]}
                aria-hidden='true'
              />
            </Stack>
          </ToggleButton>
        )}
      </Stack>
    )
  }

  return <BenefitsLoading />
}

const Title = styled(Typography)`
  letter-spacing: ${remUnit(0.028)};
` as typeof Typography

const Description = styled(Typography)`
  letter-spacing: 0;
` as typeof Typography

const HeroImage = styled(CardMedia)`
  width: 100%;
  height: ${unit(44.5)};
  display: block;
`

const ToggleButton = styled('button')`
  display: block;
  width: calc(100% - ${unit(7)});
  margin: 0 ${unit(3.5)} ${unit(3.5)};
  padding: ${unit(5.5)} ${unit(3.5)} ${unit(3.5)};
  border: none;
  border-top: ${unit(0.25)} dashed ${COLORS.vaalea[800]};
  border-radius: 0;
  background: none;
  appearance: none;
  font: inherit;
  color: inherit;
  text-align: inherit;
  cursor: pointer;
`

const ToggleLabel = styled(Typography)`
  text-decoration: underline;
  line-height: ${remUnit(5)};
`
